import { z } from 'zod';
import { ApiResponse, ErrorResponse } from '@/shared/types';
import { ClipInfo } from '@/shared/apis/editor/clip-types';
import { axiosClient } from '@/shared';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DtoEmotion } from '@/shared/constants';
import { handleTtsError } from '@/shared/lib/tts-blocked-error';

export const PostTtsGenerateRequestSchema = z.object({
  projectId: z.number(),
  /** 동시성 격리용 scene 식별자. BE → AI 로 그대로 전달되어 AI tmp 경로를 scene 단위로 분리. */
  sceneId: z.number().optional(),
  /**
   * 멱등성 키 — React Query/axios 자동 재시도 등 **같은 mutation 의 네트워크 레이어 중복**을 BE Redis NX 락으로 차단한다.
   * 사용자의 **수동 연속 클릭**은 별도로 FE β 가드(`useIsMutating`)와 버튼 disabled 로 막는 것이 1차 방어선이며,
   * idempotencyKey 는 그 방어선을 통과한 네트워크 레이어 race 의 2차 방어선.
   * BE 는 이 값을 Redis 락 key 및 point_history.reference_key 양쪽에 그대로 사용하므로
   * `^[A-Za-z0-9_-]+$` / 128자 이하만 허용된다.
   *
   * 전송 채널: 표준 RFC 헤더 `Idempotency-Key` (BE-API M-API-2). caller 는 이 필드에 값을
   * 담아 전달하면 `postTtsGenerateApi` 가 body 에서 분리해 헤더로 전송한다.
   *
   * 검증: BE `@Pattern("^[A-Za-z0-9_-]{1,128}$")` 미러링. 단일 regex 로 빈 문자열·
   * 길이 초과·금지 문자를 zod 단계에서 조기 차단.
   */
  idempotencyKey: z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,128}$/)
    .optional(),
  text: z.string().optional(),
  clipInfos: z.any().optional(),
  // 보이스 소유권 이관 메모:
  // - 기존 FE 소스: DtoVoice 정적 submit schema.
  // - 새 소유자: BE 보이스 카탈로그와 기본값 resolver.
  // - 런타임 계약: 미확정 voice 는 생략하고, catalog 에서 확정된 voice code 만 전송한다.
  // - 제거 게이트: FE static voice scan 에 static_submit_voice_schema finding 이 남지 않음.
  voice: z.string().trim().min(1).optional(),
  speedFactor: z.number().optional(),
  pitch: z.number(),
  emotion: z.nativeEnum(DtoEmotion).optional(),
  /** Gemini 전용 자연어 스타일 지시. 다른 벤더는 무시됨. */
  style: z.string().optional(),
});

export type PostTtsGenerateRequestType = z.infer<
  typeof PostTtsGenerateRequestSchema
>;

type Response = ApiResponse<{ data: ClipInfo[] }>;

/**
 * β 가드용 mutation key 생성 — 같은 projectId 에 대한 TTS 생성 요청을 `useIsMutating` 으로 추적 가능.
 * projectId 미제공 시 static key 로 폴백.
 */
export const ttsGenerateMutationKey = (projectId?: number) =>
  projectId != null ? ['tts-generate', projectId] : ['tts-generate'];

export const postTtsGenerateApi = async (body: PostTtsGenerateRequestType) => {
  try {
    const { idempotencyKey, ...payload } = body;
    if (payload.voice != null && payload.voice.trim() === '') {
      delete payload.voice;
    }
    const response = await axiosClient.post<Response>(
      '/v1.1/tts/generate',
      payload,
      idempotencyKey
        ? { headers: { 'Idempotency-Key': idempotencyKey } }
        : undefined,
    );

    return response.data;
  } catch (error) {
    handleTtsError(error, 'generate');
    return Promise.reject(error);
  }
};

export const usePostTtsGenerateApi = (projectId?: number) => {
  return useMutation<
    Response,
    AxiosError<ErrorResponse>,
    PostTtsGenerateRequestType
  >({
    mutationKey: ttsGenerateMutationKey(projectId),
    mutationFn: postTtsGenerateApi,
  });
};
